import React, { useEffect, useMemo, useState } from 'react';
import styles from './LlmSettingsTab.module.css';
import { settingsApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { hasValue, normalizeResponse } from '../settingsUtils';
import { setModelPriceOverrides } from '../../../lib/tokenCost';
import ModelPricingTable from './ModelPricingTable';
import { SettingsMap } from '../../../types/webchatTabsTypes';


type FieldType = 'number' | 'string';

interface LlmFieldMeta {
  key: string;
  label: string;
  description: string;
  type: FieldType;
  fallbackDefault: string | number;
  isSensitive?: boolean;
  min?: number;
  step?: number | 'any';
  hideDefaultHint?: boolean;
}

const FIELD_META: LlmFieldMeta[] = [
  {
    key: 'result_max_chars',
    label: 'Max Result Characters',
    description:
      'Maximum total characters in a tool result returned to the LLM. Larger values send more data but consume more tokens.',
    type: 'number',
    fallbackDefault: 10000,
    min: 1,
    step: 1,
  },
  {
    key: 'result_max_string_length',
    label: 'Max String Length',
    description: 'Maximum length of any individual string value in a result before it is truncated.',
    type: 'number',
    fallbackDefault: 5000,
    min: 1,
    step: 1,
  },
  {
    key: 'result_max_items',
    label: 'Max Items',
    description: 'Maximum number of items shown in an array before truncation occurs.',
    type: 'number',
    fallbackDefault: 100,
    min: 1,
    step: 1,
  },
  {
    key: 'result_max_iterations',
    label: 'Max Iterations',
    description:
      'Maximum number of binary-search iterations when fitting a result within the character limit.',
    type: 'number',
    fallbackDefault: 8,
    min: 1,
    step: 1,
  },
  {
    key: 'CLAUDE_API_KEY',
    label: 'Claude API Key',
    description: 'Anthropic API key used for Claude requests.',
    type: 'string',
    fallbackDefault: '',
    isSensitive: true,
    hideDefaultHint: false,
  },
  {
    key: 'CLAUDE_MODEL',
    label: 'Claude Model',
    description: 'Claude model name used by the LLM service.',
    type: 'string',
    fallbackDefault: 'claude-sonnet-4-20250514',
    hideDefaultHint: true,
  },
  {
    key: 'CLAUDE_URL',
    label: 'Claude URL',
    description: 'Anthropic Messages API URL.',
    type: 'string',
    fallbackDefault: 'https://api.anthropic.com/v1/messages',
    hideDefaultHint: true,
  },
  {
    key: 'CLAUDE_MAX_TOKENS',
    label: 'Claude Max Tokens',
    description: 'Maximum output tokens for Claude responses.',
    type: 'number',
    fallbackDefault: 1000,
    min: 1,
    step: 1,
    hideDefaultHint: true,
  },
  {
    key: 'CLAUDE_TEMPERATURE',
    label: 'Claude Temperature',
    description: 'Sampling temperature for Claude responses.',
    type: 'number',
    fallbackDefault: 0.7,
    min: 0,
    step: 'any',
    hideDefaultHint: true,
  },
  {
    key: 'CLAUDE_API_VERSION',
    label: 'Claude API Version',
    description: 'Anthropic API version header value.',
    type: 'string',
    fallbackDefault: '2023-06-01',
    hideDefaultHint: true,
  },
];




type InnerTab = 'settings' | 'pricing';

const LlmSettingsTab: React.FC = () => {
  const [innerTab, setInnerTab] = useState<InnerTab>('settings');
  const [settings, setSettings] = useState<SettingsMap | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [initialDraft, setInitialDraft] = useState<Record<string, string>>({});
  const [sensitiveConfigured, setSensitiveConfigured] = useState<Record<string, boolean>>({});
  const [clearedSensitive, setClearedSensitive] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modelPricesJson, setModelPricesJson] = useState<string | undefined>(undefined);

  const loadLlmSettings = async (showErrorToast = true) => {
    try {
      const raw = await settingsApi.getLlmSettings();
      const fromServer = normalizeResponse(raw);
      const normalized: SettingsMap = { ...fromServer };
      const initial: Record<string, string> = {};
      const sensitiveState: Record<string, boolean> = {};

      for (const { key, fallbackDefault, isSensitive } of FIELD_META) {
        const rawValue = fromServer[key]?.value;
        const rawDefault = fromServer[key]?.default;
        const def = hasValue(rawDefault) ? (rawDefault as string | number) : fallbackDefault;
        const fieldIsSensitive = fromServer[key]?.isSensitive ?? isSensitive;

        if (fieldIsSensitive) {
          sensitiveState[key] = hasValue(rawValue) || hasValue(rawDefault);
          normalized[key] = { value: '', default: '' };
          initial[key] = '';
        } else {
          const value = hasValue(rawValue) ? (rawValue as string | number) : def;
          normalized[key] = { value, default: def };
          initial[key] = String(value);
        }
      }

      // Load model prices override from settings and apply globally
      const modelPricesRaw = fromServer['MODEL_PRICES']?.value;
      if (modelPricesRaw && typeof modelPricesRaw === 'string') {
        try {
          const parsed = JSON.parse(modelPricesRaw);
          setModelPriceOverrides(parsed);
          setModelPricesJson(modelPricesRaw);
        } catch { /* ignore malformed JSON */ }
      }

      setSettings(normalized);
      setDraft(initial);
      setInitialDraft(initial);
      setSensitiveConfigured(sensitiveState);
      setClearedSensitive({});
    } catch {
      if (showErrorToast) toast.error('Failed to load LLM settings');
      throw new Error('Failed to load LLM settings');
    }
  };

  useEffect(() => {
    loadLlmSettings()
      .finally(() => setLoading(false));
  }, []);

  const isDirty = useMemo(() => {
    return FIELD_META.some(({ key }) => draft[key] !== (initialDraft[key] ?? '') || clearedSensitive[key] === true);
  }, [draft, initialDraft, clearedSensitive]);

  const handleSensitiveChange = (key: string, value: string) => {
    setDraft(d => ({ ...d, [key]: value }));
    if (value.trim()) {
      setClearedSensitive(c => ({ ...c, [key]: false }));
    }
  };

  const handleClearSensitive = (key: string) => {
    setDraft(d => ({ ...d, [key]: '' }));
    setClearedSensitive(c => ({ ...c, [key]: true }));
  };

  const handleSaveAll = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const { key, label, type, min, isSensitive } of FIELD_META) {
      const raw = draft[key]?.trim();
      const clearRequested = clearedSensitive[key] === true;
      if (!raw) {
        if (isSensitive && (sensitiveConfigured[key] || clearRequested)) {
          continue;
        }
        toast.error(`${label}: value is required`);
        return;
      }
      if (type === 'number') {
        const num = Number(raw);
        if (isNaN(num)) {
          toast.error(`${label}: please enter a valid number`);
          return;
        }
        if (min !== undefined && num < min) {
          toast.error(`${label}: must be greater than or equal to ${min}`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const updates: Record<string, string> = {};
      for (const { key, type, isSensitive } of FIELD_META) {
        const raw = draft[key]?.trim() ?? '';
        const previous = initialDraft[key] ?? '';
        const clearRequested = clearedSensitive[key] === true;
        if (isSensitive) {
          if (!raw && !clearRequested) continue;
          if (raw) {
            updates[key] = raw;
          } else if (clearRequested) {
            updates[key] = '';
          }
          continue;
        }
        if (raw === previous) continue;
        if (type === 'number') {
          updates[key] = String(Number(raw));
        } else {
          updates[key] = raw;
        }
      }

      if (Object.keys(updates).length === 0) {
        toast.success('No changes to save');
        setSaving(false);
        return;
      }

      await Promise.all(
        Object.entries(updates).map(([key, value]) => settingsApi.setValue(key, { value }))
      );
      await loadLlmSettings(false);
      toast.success('LLM settings saved');
    } catch {
      toast.error('Failed to save LLM settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!settings) return;
    const updates: Record<string, string> = {};
    for (const { key, fallbackDefault, isSensitive } of FIELD_META) {
      if (isSensitive) continue;
      const currentDefault = settings[key]?.default ?? fallbackDefault;
      updates[key] = String(currentDefault);
    }
    if (Object.keys(updates).length === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(updates).map(([key, value]) => settingsApi.setValue(key, { value }))
      );
      await loadLlmSettings(false);
      toast.success('Reset non-sensitive settings to defaults');
    } catch {
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading…</div>;
  if (!settings) return null;

  return (
    <div className={styles.tab}>
      <div className={styles.header}>
        <div>
          <h2>System LLM</h2>
          <p>Controls tool-result truncation and LLM-related infrastructure settings.</p>
        </div>
      </div>

      <div className={styles.innerTabs}>
        <button
          className={`${styles.innerTab} ${innerTab === 'settings' ? styles.innerTabActive : ''}`}
          onClick={() => setInnerTab('settings')}
        >
          Settings
        </button>
        <button
          className={`${styles.innerTab} ${innerTab === 'pricing' ? styles.innerTabActive : ''}`}
          onClick={() => setInnerTab('pricing')}
        >
          Model Pricing
        </button>
      </div>

      {innerTab === 'settings' && (
        <form className={styles.form} onSubmit={handleSaveAll}>
          {FIELD_META.map(({ key, label, description, type, isSensitive, min, step, hideDefaultHint }) => (
            <div key={key} className={styles.fieldRow}>
              <div className={styles.fieldHeader}>
                <span className={styles.fieldLabel}>{label}</span>
                <span className={styles.keyBadge}>{key}</span>
              </div>
              <p className={styles.fieldDescription}>{description}</p>
              <div className={styles.fieldInputRow}>
                <div className={styles.fieldInputControls}>
                  <SecureInput
                    fieldType={type === 'number' ? 'number' : 'text'}
                    autoComplete={isSensitive ? 'new-password' : 'new-default'}
                    value={draft[key] ?? ''}
                    placeholder={isSensitive && sensitiveConfigured[key] ? 'Enter a new value to replace the configured key' : undefined}
                    min={type === 'number' ? (min ?? 1) : undefined}
                    step={type === 'number' ? (step ?? 1) : undefined}
                    onChange={v => isSensitive ? handleSensitiveChange(key, v) : setDraft(d => ({ ...d, [key]: v }))}
                  />
                  {isSensitive && sensitiveConfigured[key] && (
                    <button
                      type="button"
                      className={styles.clearBtn}
                      onClick={() => handleClearSensitive(key)}
                      disabled={saving}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {!hideDefaultHint && (
                  <span className={styles.defaultHint}>
                    {isSensitive
                      ? (clearedSensitive[key]
                        ? '(will be cleared)'
                        : sensitiveConfigured[key]
                          ? '(configured)'
                          : '(not set)')
                      : `default: ${settings[key]?.default}`}
                  </span>
                )}
              </div>
            </div>
          ))}

          <div className={styles.footer}>
            <button type="submit" className={styles.saveBtn} disabled={!isDirty || saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className={styles.resetBtn} disabled={saving} onClick={handleResetAll}>
              Reset all to defaults
            </button>
          </div>
        </form>
      )}

      {innerTab === 'pricing' && (
        <ModelPricingTable initialJson={modelPricesJson} />
      )}
    </div>
  );
};

export default LlmSettingsTab;
